import { Component, DestroyRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { DateTime } from "luxon";

import { AddStudent } from '../../classes/add-student';
import { Award } from '../../classes/award';
import { Applicant, ScholarshipManagerTable } from '../../classes/scholarship-manager-table';
import { AppService } from '../../services/app.service';
import { SettingsService } from '../../services/settings.service';
import { SnackBarService } from '@polk_county_public_schools/ngx-pcps-dialogs/snackbar';
import { UserInfo, UserService } from '../../services/user.service';
import { AddStudentModalComponent } from '../modals/add-student-modal/add-student-modal.component';
import { WaitModalComponent } from '../modals/wait-modal/wait-modal.component';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { SchoolYearPipe } from '../../pipes/schoolyear/schoolyear.pipe';
import { MatButtonModule } from '@angular/material/button';
import { ErrorService } from '../../services/error.service';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
    selector: 'app-manage-scholarship',
    templateUrl: './manage-scholarship.component.html',
    styleUrls: ['./manage-scholarship.component.scss'],
    imports: [
        RouterModule,
        CommonModule,
        MatIconModule,
        MatCheckboxModule,
        MatTableModule,
        MatDividerModule,
        SchoolYearPipe,
        MatButtonModule,
        MatTooltipModule
    ]
})
export class ManageScholarshipComponent implements OnInit {
    protected readonly destroyRef = inject(DestroyRef);
    protected readonly appSvc = inject(AppService);
    protected readonly matDialog = inject(MatDialog);
    protected readonly route = inject(ActivatedRoute);
    protected readonly settingSvc = inject(SettingsService);
    protected readonly snackBarSvc = inject(SnackBarService);
    protected readonly errorSvc = inject(ErrorService);
    protected readonly userSvc = inject(UserService);
    user: UserInfo;
    dataSource: MatTableDataSource<Applicant>;
    @ViewChild(MatSort) sort: MatSort;
    displayedColumns: string[] = ['SchoolNumber', 'Name', 'GPA', 'SAT', 'ACT', 'CLT', 'CsHours', 'WorkHours', 'Finalist', 'Awarded', 'Action'];
    schoolYear: string;
    scholarshipID: number;
    scholarshipInfo: ScholarshipManagerTable;
    anyFinalists: boolean;
    availableStudents: string[];

    ngOnInit() {
        this.user = this.userSvc.User;
        this.getSettings();
        this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
            this.scholarshipID = +params['id'];
            this.getTableData();
            this.getAvailableStudents();
        });
    }

    getSettings() {
        this.settingSvc.SettingsSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(settings => {
            if (settings == null) return;
            this.schoolYear = settings.find(f => f.Label === 'School year (scholarship admin)').Value;
        });
    }

    getAvailableStudents() {
        this.appSvc.GetAvailableStudents(this.scholarshipID).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: res => {
                this.availableStudents = res;
            }, error: error => {
                this.errorSvc.HandleServiceError(error);
            }
        });
    }

    getTableData() {
        this.appSvc.GetScholarshipManagerTable(this.scholarshipID).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: res => {
                this.scholarshipInfo = res;
                this.dataSource = new MatTableDataSource(res.Applicants);
                this.dataSource.sort = this.sort;
                this.anyFinalists = res.Applicants.some(a => a.Finalist);
            }, error: error => {
                this.errorSvc.HandleServiceError(error);
            }
        });
    }

    onClick(appID: number, awarded: boolean) {
        let dto: Award = {
            AppID: appID,
            ScholarshipID: this.scholarshipID,
            Awarded: awarded
        };

        this.appSvc.AwardScholarship(dto).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: res => {
                this.dataSource = new MatTableDataSource(res.Applicants);
                this.dataSource.sort = this.sort;
                this.anyFinalists = res.Applicants.some(a => a.Finalist);
            }, error: error => {
                this.errorSvc.HandleServiceError(error);
            }
        });
    }

    addStudent() {
        let dialog = this.matDialog.open<AddStudentModalComponent>(AddStudentModalComponent, {
            disableClose: true,
            data: { availableStudents: this.availableStudents }
        });

        dialog.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
            if (res == null || res == '') {
                return;
            }

            let dto: AddStudent = {
                ScholarshipID: this.scholarshipID,
                StudentID: res
            };

            this.appSvc.AddStudent(dto).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
                next: res => {
                    this.dataSource = new MatTableDataSource(res.Applicants);
                    this.dataSource.sort = this.sort;
                    this.anyFinalists = res.Applicants.some(a => a.Finalist);
                }, error: error => {
                    this.errorSvc.HandleServiceError(error);
                }
            });
        });
    }

    finalistDetails() {
        let wait = this.matDialog.open(WaitModalComponent);
        this.appSvc.FinalistDetailsReport(this.scholarshipID).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: res => {
                wait.close();
                this.saveBlobToFile(res.body, this.scholarshipInfo.ScholarshipName + '_Finalists_' + DateTime.now().toFormat('MMM_d_y') + '.zip');
            }, error: error => {
                wait.close();
                this.errorSvc.HandleServiceError(error);
            }
        });
    }

    downloadPDF(app: Applicant) {
        let wait = this.matDialog.open(WaitModalComponent);

        this.appSvc
            .ApplicationDetailsReport(false, app.AppID)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    wait.close();
                    this.saveBlobToFile(
                        res.body,
                        app.FirstName.trim() +
                        '_' +
                        app.LastName.trim() +
                        '_Application_' +
                        DateTime.now().toFormat('MMM_d_y')
                    );
                },
                error: (error) => {
                    wait.close();
                    this.errorSvc.HandleServiceError(error);
                }
            });
    }

    saveBlobToFile(blob: Blob, fileName: string): void {
        //setup temporary in-memory URL and html tag
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);

        //programattically click on tag for user
        anchor.click();

        //delete tag and URL
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(url);
    }

}
